/* eslint-disable sort-keys-fix/sort-keys-fix -- CSS-in-JS pseudo-selector keys use semantic ordering */
// MUI v4 Checkbox, NS/Tractie checkbox-pendant overrides.
// Tractie source: checkbox. Checked-state gebruikt success-cluster (NIET interactive-blue).
//
// Token references (Tractie -> NS):
//   - default border: --tractie-color-border-input-default -> --ns-color-border-input-default (cluster 3)
//   - hover border:   --tractie-color-border-input-hover   -> --ns-color-border-input-hover   (cluster 3)
//   - bg unchecked:   --tractie-color-background-input     -> --ns-color-bg-input             (cluster 1)
//   - bg checked:     --tractie-color-background-success-default -> --ns-color-bg-success-default (cluster 1, Q2)
//   - border checked: --tractie-color-border-success       -> --ns-color-border-success       (cluster 3, Q2)
//                     [intentionally NOT applied as CSS-value — see Q2-resolution below]
//   - check-mark:     --tractie-color-content-default-inverse -> --ns-color-fg-default-inverse (cluster 2)
//   - radius:         --tractie-border-radius-s (3px)      -> --ns-radius-s                   (cluster 5)
//   - icon-size:      1rem                                 -> --ns-control-icon-size-s        (cluster 13)
//
// MUI v4 Checkbox DOM: <span class="MuiCheckbox-root"><span class="MuiIconButton-label"><svg>...</svg></span></span>
// MUI default: colorPrimary checked = primary.main (blauw). Tractie deviation: success-green (Q2).
// MUI default: hover = rgba(0,0,0,0.04). Tractie deviation: alleen border-color flip, geen bg-tint.
// MUI default: root padding 9px. Tractie deviation: 0 (Tractie checkbox is 16x16 zonder hit-target padding;
//   hit-target wordt door label-area uitgebreid; FormControlLabel zorgt voor click-area).
//
// Q2-resolution + Tractie-spirit-derivation (checked border):
//   Tractie-spec specificeert voor checked-state TWEE properties: bg = success-default EN
//   border = --ns-color-border-success. MUI v4 rendert echter `CheckBoxIcon` als één
//   filled SVG-path (single-fill rect met afgeronde hoeken) — er is GEEN separaat
//   border-element of stroke-target waarop een border-color toegepast kan worden.
//   De `color`-property in MUI's Checkbox-override stuurt de SVG `fill` aan, en die
//   fill representeert in Tractie-spirit beide rollen tegelijk (visuele afgrenzing
//   = de gevulde vorm zelf). Daarom wordt `--ns-color-border-success` intentionally
//   NIET als CSS-value toegepast in de `'&$checked'`-block. Deze derivation is
//   gelockt door een design-decision test in mui-checkbox-overrides.test.ts.

export const MuiCheckboxLightOverrides = {
  root: {
    color: 'var(--ns-color-border-input-default)',
    padding: 'var(--ns-space-1)',
    '&:hover': {
      backgroundColor: 'transparent',
      color: 'var(--ns-color-border-input-hover)',
    },
    '&$disabled': {
      opacity: 0.5,
      pointerEvents: 'none' as const,
    },
  },

  // Q2: checked-state = success-cluster (Tractie-spec, NIET MUI default primary-blue)
  //
  // Bug fix 2026-05-27 (task backstage-developer-portal-fszu, discovered op
  // /search Lifecycle filter): zelfde IconButton-leak als colorSecondary.
  // MuiCheckbox krijgt bij color="primary" beide MuiCheckbox-colorPrimary
  // EN MuiIconButton-colorPrimary classes; onze IconButton.colorPrimary
  // override (T3.12) zet backgroundColor: var(--ns-color-bg-interactive-default)
  // wat leakt als groot blauw 40x40 vlak rond elke checkbox in
  // SearchFilter.Checkbox. Reset hier expliciet backgroundColor + color zodat
  // de Checkbox-rol wint van de IconButton-rol (parallel aan colorSecondary).
  colorPrimary: {
    backgroundColor: 'transparent',
    color: 'var(--ns-color-border-input-default)',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    // Tractie-spirit-derivation: Tractie-spec vraagt bg + border voor checked.
    // MUI v4 CheckBoxIcon is een single filled SVG (geen separaat border-element),
    // dus `color` (= SVG fill) representeert beide rollen. Token
    // `--ns-color-border-success` wordt intentionally niet als CSS-value gezet.
    '&$checked': {
      color: 'var(--ns-color-bg-success-default)',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    '&$disabled': {
      opacity: 0.5,
    },
  },

  // Q2: secondary kleurvariant zelfde Tractie-mapping (success). MUI biedt geen
  // visueel onderscheid tussen primary/secondary in Tractie's spec.
  //
  // Bug fix 2026-05-25 (task backstage-developer-portal-2764):
  // MuiCheckbox extends IconButton onder de motorkap; bij color="secondary"
  // krijgt het element BEIDE classes (MuiCheckbox-colorSecondary EN
  // MuiIconButton-colorSecondary). Onze IconButton.colorSecondary override
  // (T3.12) zet backgroundColor: var(--ns-color-bg-interactive-default) → die
  // leakt naar Checkbox als groot blauw 40x40 vlak. Reset hier expliciet
  // backgroundColor + color zodat de Checkbox-rol wint van de IconButton-rol.
  colorSecondary: {
    backgroundColor: 'transparent',
    color: 'var(--ns-color-border-input-default)',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&$checked': {
      color: 'var(--ns-color-bg-success-default)',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    '&$disabled': {
      opacity: 0.5,
    },
  },

  checked: {},
  disabled: {},
  indeterminate: {
    color: 'var(--ns-color-bg-success-default)',
  },
} as const;

// Dark spreads Light: token-driven (success en input-cluster mode-flippen via tokens.css).
export const MuiCheckboxDarkOverrides = {
  ...MuiCheckboxLightOverrides,
} as const;
